/**
 * MINIMOG / ZEST — CART DRAWER PRO  (v1.1)
 * -------------------------------------------------------------------------
 * <m-cart-custom-fields> saves Sender Number, Delivery Date & Time and
 * Photo Verification as CART ATTRIBUTES via /cart/update.js, so the values
 * travel through checkout and appear on the order in Shopify admin.
 *
 * v1.1 fixes
 *  - Date + time panel is now in-flow, so it can never be clipped by the
 *    scrolling drawer footer.
 *  - Save applies reliably: the date defaults to the earliest allowed day and
 *    the panel shows its own message instead of the generic footer error.
 *  - Attribute writes are queued and merged, so two quick edits can no longer
 *    overwrite each other.
 *  - The saved date is parsed back into the picker after the drawer re-renders.
 *  - The photo checkbox no longer re-posts its default on every cart update.
 * -------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var UPDATE_URL = ROOT.replace(/\/$/, '') + '/cart/update.js';

  var MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function toISODate(date) {
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }

  /* Format an ISO yyyy-mm-dd string for display. */
  function formatDate(iso, format) {
    if (!iso) return '';
    var p = iso.split('-');
    if (p.length !== 3) return iso;

    if (format === 'iso') return iso;
    if (format === 'slash') return p[2] + '/' + p[1] + '/' + p[0];

    var monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var mIndex = Number(p[1]) - 1;
    var month = monthNames[mIndex] || p[1];
    return Number(p[2]) + ' ' + month + ' ' + p[0];
  }

  /* Turn a previously displayed date back into yyyy-mm-dd. */
  function parseDisplayedDate(text) {
    if (!text) return '';
    var value = text.trim();

    var isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) return value;

    var slashMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) return slashMatch[3] + '-' + pad(Number(slashMatch[2])) + '-' + pad(Number(slashMatch[1]));

    var mediumMatch = value.match(/^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/);
    if (mediumMatch) {
      var idx = MONTHS.indexOf(mediumMatch[2].slice(0, 3).toLowerCase());
      if (idx > -1) return mediumMatch[3] + '-' + pad(idx + 1) + '-' + pad(Number(mediumMatch[1]));
    }

    return '';
  }

  function format12h(value) {
    if (!value) return '';
    var parts = value.split(':');
    if (parts.length < 2) return value;
    var h = Number(parts[0]);
    var suffix = h >= 12 ? 'PM' : 'AM';
    var hour = h % 12;
    if (hour === 0) hour = 12;
    return hour + ':' + parts[1] + ' ' + suffix;
  }

  /* ---------------------------------------------------------------------
     Cart-busy tracking.
     The theme fires its own /cart/change.js request when a quantity changes
     or a line is removed, then re-renders the drawer from that response.
     A cart write of ours landing in the middle of that made the drawer show
     stale quantities and a stale subtotal, so we hold our writes until the
     theme is done.
     --------------------------------------------------------------------- */
  var busyUntil = 0;

  function markBusy() {
    busyUntil = Date.now() + 1500;
  }

  // Bubble phase, and only for elements inside the cart UI. An earlier build
  // used document-wide capture listeners, which sat in front of every click on
  // the storefront including add-to-cart.
  function inCartUI(node) {
    return !!(node && node.closest && node.closest('m-cart-drawer, m-cart, m-cart-items'));
  }

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!inCartUI(t)) return;
    if (t.closest('m-cart-remove-button, .m-quantity__button, [is="m-cart-discount-remove"]')) markBusy();
  });

  document.addEventListener('change', function (event) {
    var t = event.target;
    if (!inCartUI(t)) return;
    if (t.closest('m-quantity-input')) markBusy();
  });

  document.addEventListener('cart:updated', function () {
    busyUntil = 0;
  });

  function waitForIdle() {
    return new Promise(function (resolve) {
      var attempts = 0;
      (function check() {
        if (Date.now() >= busyUntil || attempts > 20) return resolve();
        attempts++;
        setTimeout(check, 150);
      })();
    });
  }

  if (!customElements.get('m-cart-custom-fields')) {
    class MCartCustomFields extends HTMLElement {
      constructor() {
        super();
        this.queue = Promise.resolve();
        this.pending = null;
        this.onSenderInput = debounce(this.handleSenderInput.bind(this), 600);
      }

      connectedCallback() {
        try {
          this.setup();
        } catch (error) {
          // A throw here happens mid-innerHTML while the theme replaces the
          // drawer. Never let our failure take the cart down with it.
          console.error('[Cart Drawer Pro] init failed, cart left untouched:', error);
        }
      }

      setup() {
        this.senderInput = this.querySelector('[data-cart-field="sender"]');
        this.deliveryInput = this.querySelector('[data-cart-field="delivery"]');
        this.photoInput = this.querySelector('[data-cart-field="photo"]');
        this.globalError = this.querySelector('[data-fields-global-error]');

        this.keys = {
          sender: this.dataset.senderKey || 'Sender Number',
          delivery: this.dataset.deliveryKey || 'Delivery Date & Time',
          photo: this.dataset.photoKey || 'Photo Verification'
        };

        this.dateFormat = this.dataset.dateFormat || 'medium';
        this.photoCheckedValue = this.dataset.photoCheckedValue || 'Yes';
        this.photoUncheckedValue = this.dataset.photoUncheckedValue || 'No';
        this.saveWhenUnchecked = this.dataset.saveEmpty === 'true';
        this.blockCheckout = this.dataset.blockCheckout === 'true';
        this.errorText = this.dataset.errorText || 'Please complete the required fields above.';

        // What the cart already holds, so we can tell real edits from a
        // simple re-render and only write what actually changed.
        this.saved = {};
        if (this.senderInput) this.saved[this.keys.sender] = (this.senderInput.defaultValue || '').trim();
        if (this.deliveryInput) this.saved[this.keys.delivery] = (this.deliveryInput.defaultValue || '').trim();
        if (this.photoInput) {
          this.saved[this.keys.photo] =
            this.dataset.photoStored === 'true'
              ? this.photoInput.checked
                ? this.photoCheckedValue
                : this.photoUncheckedValue
              : '';
        }
        this._flushed = false;

        this.bindSender();
        this.bindDelivery();
        this.bindPhoto();
        this.bindCheckout();
      }

      disconnectedCallback() {
        if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
        if (this._onCheckout && this.checkoutButtons) {
          this.checkoutButtons.forEach(
            function (btn) {
              btn.removeEventListener('click', this._onCheckout, true);
            }.bind(this)
          );
        }
      }

      /* ------------------------------------------------------- Sender field */
      bindSender() {
        if (!this.senderInput) return;
        this.senderInput.addEventListener('input', this.onSenderInput);
        this.senderInput.addEventListener('change', this.handleSenderInput.bind(this));
        this.senderInput.addEventListener('blur', this.handleSenderInput.bind(this));

        // The input carries form="cart-drawer-form", so a bare Enter would
        // submit the cart form and jump straight to checkout. Save instead.
        this.senderInput.addEventListener(
          'keydown',
          function (event) {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.handleSenderInput();
              this.senderInput.blur();
            }
          }.bind(this)
        );
      }

      handleSenderInput() {
        if (!this.senderInput) return;
        var value = this.senderInput.value.trim();
        if (value === this._lastSender) return;
        this._lastSender = value;
        this.clearError(this.senderInput);
        var payload = {};
        payload[this.keys.sender] = value;
        this.saveAttributes(payload);
      }

      /* ----------------------------------------------------- Delivery field */
      bindDelivery() {
        this.picker = this.querySelector('[data-delivery-picker]');
        if (!this.picker) return;

        this.displayBtn = this.picker.querySelector('[data-delivery-display]');
        this.displayText = this.picker.querySelector('[data-delivery-display-text]');
        this.panel = this.picker.querySelector('[data-delivery-panel]');
        this.panelError = this.picker.querySelector('[data-panel-error]');
        this.dateEl = this.picker.querySelector('[data-delivery-date]');
        this.slotEl = this.picker.querySelector('[data-delivery-slot]');
        this.timeEl = this.picker.querySelector('[data-delivery-time]');
        this.saveBtn = this.picker.querySelector('[data-delivery-save]');
        this.clearBtn = this.picker.querySelector('[data-delivery-clear]');

        this.placeholder = (this.displayBtn && this.displayBtn.dataset.placeholder) || '';

        this.applyDateBounds();
        this.prefillFromValue();

        if (this.displayBtn) this.displayBtn.addEventListener('click', this.togglePanel.bind(this));
        if (this.saveBtn) this.saveBtn.addEventListener('click', this.saveDelivery.bind(this));
        if (this.clearBtn) this.clearBtn.addEventListener('click', this.clearDelivery.bind(this));

        // Clear the panel warning as soon as the shopper fixes the input.
        [this.dateEl, this.slotEl, this.timeEl].forEach(
          function (el) {
            if (el) el.addEventListener('change', this.hidePanelError.bind(this));
          }.bind(this)
        );

        this._onDocClick = function (event) {
          if (!this.picker.contains(event.target)) this.closePanel();
        }.bind(this);
        document.addEventListener('click', this._onDocClick);

        this.addEventListener(
          'keydown',
          function (event) {
            if (event.key === 'Escape') this.closePanel();
          }.bind(this)
        );
      }

      applyDateBounds() {
        if (!this.dateEl) return;
        var minDays = parseInt(this.dateEl.dataset.minDays, 10);
        var maxDays = parseInt(this.dateEl.dataset.maxDays, 10);
        if (isNaN(minDays)) minDays = 0;
        if (isNaN(maxDays)) maxDays = 60;

        var min = new Date();
        min.setDate(min.getDate() + minDays);
        var max = new Date();
        max.setDate(max.getDate() + maxDays);

        this.minISO = toISODate(min);
        this.dateEl.min = this.minISO;
        this.dateEl.max = toISODate(max);
      }

      /* Restore the picker state from the value already stored in the cart. */
      prefillFromValue() {
        var stored = this.deliveryInput ? this.deliveryInput.value.trim() : '';

        if (!stored) {
          // Nothing saved yet: pre-select the earliest allowed day so Save works
          // in one tap, and pre-select the only slot when there is just one.
          if (this.dateEl && !this.dateEl.value && this.minISO) this.dateEl.value = this.minISO;
          if (this.slotEl && this.slotEl.options.length === 2) this.slotEl.selectedIndex = 1;
          return;
        }

        var parts = stored.split('|');
        var datePart = (parts[0] || '').trim();
        var timePart = (parts[1] || '').trim();

        var iso = parseDisplayedDate(datePart);
        if (this.dateEl) this.dateEl.value = iso || this.minISO || '';

        if (this.slotEl && timePart) {
          for (var i = 0; i < this.slotEl.options.length; i++) {
            if (this.slotEl.options[i].value === timePart) {
              this.slotEl.selectedIndex = i;
              break;
            }
          }
        }
      }

      togglePanel(event) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.panel) return;
        if (this.panel.hasAttribute('hidden')) {
          this.openPanel();
        } else {
          this.closePanel();
        }
      }

      openPanel() {
        if (!this.panel) return;
        this.panel.removeAttribute('hidden');
        if (this.displayBtn) this.displayBtn.setAttribute('aria-expanded', 'true');
        // The footer scrolls, so make sure the whole panel is in view.
        requestAnimationFrame(
          function () {
            try {
              this.panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            } catch (e) {
              this.panel.scrollIntoView(false);
            }
          }.bind(this)
        );
      }

      closePanel() {
        if (!this.panel) return;
        this.panel.setAttribute('hidden', '');
        this.hidePanelError();
        if (this.displayBtn) this.displayBtn.setAttribute('aria-expanded', 'false');
      }

      showPanelError(message) {
        if (!this.panelError) return;
        this.panelError.textContent = message;
        this.panelError.removeAttribute('hidden');
      }

      hidePanelError() {
        if (!this.panelError) return;
        this.panelError.textContent = '';
        this.panelError.setAttribute('hidden', '');
      }

      buildDeliveryValue() {
        var datePart = this.dateEl && this.dateEl.value ? formatDate(this.dateEl.value, this.dateFormat) : '';
        var timePart = '';
        if (this.slotEl && this.slotEl.value) {
          timePart = this.slotEl.value;
        } else if (this.timeEl && this.timeEl.value) {
          timePart = format12h(this.timeEl.value);
        }
        if (datePart && timePart) return datePart + ' | ' + timePart;
        return datePart || timePart || '';
      }

      saveDelivery(event) {
        event.preventDefault();
        event.stopPropagation();

        if (this.dateEl && !this.dateEl.value) {
          this.showPanelError('Please choose a date.');
          this.dateEl.focus();
          return;
        }
        if (this.slotEl && !this.slotEl.value) {
          this.showPanelError('Please choose a time slot.');
          this.slotEl.focus();
          return;
        }
        if (this.timeEl && !this.timeEl.value && !this.slotEl) {
          this.showPanelError('Please choose a time.');
          this.timeEl.focus();
          return;
        }

        var value = this.buildDeliveryValue();
        this.deliveryInput.value = value;

        if (this.displayText) this.displayText.textContent = value || this.placeholder;
        if (this.displayBtn) this.displayBtn.classList.toggle('is-placeholder', !value);

        this.hidePanelError();
        this.clearError(this.deliveryInput);
        if (this.globalError) this.globalError.setAttribute('hidden', '');
        this.closePanel();

        var payload = {};
        payload[this.keys.delivery] = value;
        this.saveAttributes(payload);
      }

      clearDelivery(event) {
        event.preventDefault();
        event.stopPropagation();

        if (this.dateEl) this.dateEl.value = '';
        if (this.slotEl) this.slotEl.selectedIndex = 0;
        if (this.timeEl) this.timeEl.value = '';

        this.deliveryInput.value = '';
        if (this.displayText) this.displayText.textContent = this.placeholder;
        if (this.displayBtn) this.displayBtn.classList.add('is-placeholder');

        this.hidePanelError();
        this.closePanel();

        var payload = {};
        payload[this.keys.delivery] = '';
        this.saveAttributes(payload);
      }

      /* -------------------------------------------------------- Photo field */
      bindPhoto() {
        if (!this.photoInput) return;
        this.photoInput.addEventListener('change', this.handlePhotoChange.bind(this));
        // Deliberately no write on load. The element re-connects on every cart
        // re-render (quantity change, remove, discount), and firing a cart
        // request there raced with the theme's own request and left the drawer
        // showing stale quantities and totals. An unsaved default is flushed
        // when the shopper checks out instead — see flushIfNeeded().
      }

      handlePhotoChange() {
        var checked = this.photoInput.checked;
        var value = checked ? this.photoCheckedValue : this.saveWhenUnchecked ? this.photoUncheckedValue : '';
        this.clearError(this.photoInput);
        this.dataset.photoStored = value ? 'true' : 'false';
        var payload = {};
        payload[this.keys.photo] = value;
        this.saveAttributes(payload);
      }

      /* ------------------------------------------------------ Cart API call */
      /**
       * Writes are merged and run one after another. Shopify replaces the whole
       * attributes object per request, so parallel calls could otherwise drop a
       * value that was set a moment earlier.
       */
      saveAttributes(attributes) {
        this.pending = Object.assign({}, this.pending || {}, attributes);
        this.classList.add('is-loading');

        this.queue = this.queue
          .then(
            function () {
              // Never write while the theme has its own cart request in flight
              // (quantity change / remove). Overlapping requests made the
              // drawer re-render from a stale cart.
              return waitForIdle();
            }
          )
          .then(
            function () {
              var payload = this.pending;
              this.pending = null;
              if (!payload) return null;

              return fetch(UPDATE_URL, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json'
                },
                body: JSON.stringify({ attributes: payload })
              })
                .then(function (response) {
                  if (!response.ok) throw new Error('Cart update failed: ' + response.status);
                  return response.json();
                })
                .then(
                  function (cart) {
                    Object.keys(payload).forEach(
                      function (key) {
                        this.saved[key] = payload[key];
                      }.bind(this)
                    );
                    document.dispatchEvent(
                      new CustomEvent('cart:attributes-updated', { detail: { cart: cart, attributes: payload } })
                    );
                    return cart;
                  }.bind(this)
                );
            }.bind(this)
          )
          .catch(function (error) {
            console.error('[Cart Drawer Pro] Unable to save cart attributes:', error);
          })
          .finally(
            function () {
              if (!this.pending) this.classList.remove('is-loading');
            }.bind(this)
          );

        return this.queue;
      }

      /* Current value of every field, keyed by its cart attribute name. */
      currentValues() {
        var values = {};
        if (this.senderInput) values[this.keys.sender] = this.senderInput.value.trim();
        if (this.deliveryInput) values[this.keys.delivery] = this.deliveryInput.value.trim();
        if (this.photoInput) {
          values[this.keys.photo] = this.photoInput.checked
            ? this.photoCheckedValue
            : this.saveWhenUnchecked
            ? this.photoUncheckedValue
            : '';
        }
        return values;
      }

      /* Anything typed or ticked that has not reached the cart yet. */
      unsavedValues() {
        var current = this.currentValues();
        var diff = {};
        var found = false;
        Object.keys(current).forEach(
          function (key) {
            if (current[key] !== (this.saved[key] || '')) {
              diff[key] = current[key];
              found = true;
            }
          }.bind(this)
        );
        return found ? diff : null;
      }

      /* --------------------------------------------------------- Validation */
      bindCheckout() {
        // Always bound: even with validation disabled we still need the flush
        // so a default-ticked box reaches the order.
        var footer = this.closest('#MinimogCartDrawerFooter') || this.closest('#MinimogCartFooter') || document;
        this.checkoutButtons = Array.prototype.slice.call(
          footer.querySelectorAll('[name="checkout"], .additional-checkout-buttons button, .additional-checkout-buttons [role="button"]')
        );

        this._onCheckout = this.handleCheckoutClick.bind(this);
        this.checkoutButtons.forEach(
          function (btn) {
            btn.addEventListener('click', this._onCheckout, true);
          }.bind(this)
        );
      }

      handleCheckoutClick(event) {
        if (!this.blockCheckout || this.validate()) {
          this.flushIfNeeded(event);
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (this.globalError) {
          this.globalError.textContent = this.errorText;
          this.globalError.removeAttribute('hidden');
        }

        var firstInvalid = this.querySelector('.has-error');
        if (firstInvalid) {
          try {
            firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          } catch (e) {
            firstInvalid.scrollIntoView();
          }
          var focusable = firstInvalid.querySelector('input:not([type="hidden"]), button, select');
          if (focusable) focusable.focus({ preventScroll: true });
        }
      }

      /**
       * Nothing is written to the cart on load, so a default-ticked checkbox or
       * a value typed a moment ago may still be unsaved. Save it once, then let
       * the original click through.
       */
      flushIfNeeded(event) {
        if (this._flushed) return;

        var unsaved = this.unsavedValues();
        if (!unsaved) return;

        var target = event.currentTarget || event.target;
        event.preventDefault();
        event.stopPropagation();

        this._flushed = true;
        this.saveAttributes(unsaved).then(function () {
          if (target && typeof target.click === 'function') target.click();
        });
      }

      validate() {
        var valid = true;
        if (this.globalError) this.globalError.setAttribute('hidden', '');

        var required = this.querySelectorAll('[data-required="true"]');
        Array.prototype.forEach.call(
          required,
          function (field) {
            var ok = field.type === 'checkbox' ? field.checked : field.value.trim() !== '';

            if (ok && field.type !== 'checkbox' && field.pattern) {
              try {
                ok = new RegExp('^(?:' + field.pattern + ')$').test(field.value.trim());
              } catch (e) {
                /* ignore a malformed pattern from theme settings */
              }
            }

            if (ok) {
              this.clearError(field);
            } else {
              this.showError(field, field.dataset.errorMessage || this.errorText);
              valid = false;
            }
          }.bind(this)
        );

        return valid;
      }

      showError(field, message) {
        if (!field) return;
        var row = field.closest('.m-cart-fields__row');
        if (!row) return;
        row.classList.add('has-error');
        var el = row.querySelector('[data-field-error]');
        if (el) {
          el.textContent = message;
          el.removeAttribute('hidden');
        }
      }

      clearError(field) {
        if (!field) return;
        var row = field.closest('.m-cart-fields__row');
        if (!row) return;
        row.classList.remove('has-error');
        var el = row.querySelector('[data-field-error]');
        if (el) {
          el.textContent = '';
          el.setAttribute('hidden', '');
        }
      }
    }

    customElements.define('m-cart-custom-fields', MCartCustomFields);
  }
})();
